import type { ReactNode } from "react";
import { Ban, Check, Copy, File, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@nexus/ui";
import { OrgPickerCombobox, type OrgPickerItem } from "../OrgPickerCombobox";

export type NewOrgStartMode = "blank" | "copy";

export interface NewOrgCopySections {
  productLineIndustries: boolean;
  defaultBrand: boolean;
  roles: boolean;
  accent: boolean;
  preflight: boolean;
}

export const DEFAULT_COPY_SECTIONS: NewOrgCopySections = {
  productLineIndustries: true,
  defaultBrand: true,
  roles: true,
  accent: true,
  preflight: false,
};

const SECTION_KEYS = Object.keys(DEFAULT_COPY_SECTIONS) as Array<keyof NewOrgCopySections>;

interface CopyRow {
  key: keyof NewOrgCopySections;
  titleKey: string;
  titleDefault: string;
  detail: string;
}

interface NewOrgCopyFromSectionProps {
  mode: NewOrgStartMode;
  onModeChange: (mode: NewOrgStartMode) => void;
  sections: NewOrgCopySections;
  onSectionChange: (key: keyof NewOrgCopySections, checked: boolean) => void;
  onSelectAll: () => void;
  items: OrgPickerItem[];
  value: string;
  onValueChange: (tenantId: string) => void;
  sourceName?: string;
  sourceMeta?: string;
  sectionDetails: Record<keyof NewOrgCopySections, string>;
  loading?: boolean;
  disabled?: boolean;
}

export function NewOrgCopyFromSection({
  mode,
  onModeChange,
  sections,
  onSectionChange,
  onSelectAll,
  items,
  value,
  onValueChange,
  sourceName,
  sourceMeta,
  sectionDetails,
  loading = false,
  disabled = false,
}: NewOrgCopyFromSectionProps) {
  const { t } = useTranslation();
  const selectedCount = SECTION_KEYS.filter((key) => sections[key]).length;

  const rows: CopyRow[] = [
    {
      key: "productLineIndustries",
      titleKey: "iam.newOrgDialog.copyFrom.rowProductLine",
      titleDefault: "Product line and industries",
      detail: sectionDetails.productLineIndustries,
    },
    {
      key: "defaultBrand",
      titleKey: "iam.newOrgDialog.copyFrom.rowDefaultBrand",
      titleDefault: "Default My Brand per industry",
      detail: sectionDetails.defaultBrand,
    },
    {
      key: "roles",
      titleKey: "iam.newOrgDialog.copyFrom.rowRoles",
      titleDefault: "Roles",
      detail: sectionDetails.roles,
    },
    {
      key: "accent",
      titleKey: "iam.newOrgDialog.copyFrom.rowAccent",
      titleDefault: "Accent color and display defaults",
      detail: sectionDetails.accent,
    },
    {
      key: "preflight",
      titleKey: "iam.newOrgDialog.copyFrom.rowPreflight",
      titleDefault: "Pre Flight default brand and markets",
      detail: sectionDetails.preflight,
    },
  ];

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-col gap-0.5">
        <h4 className="text-[13px] font-semibold text-foreground">
          {t("iam.newOrgDialog.copyFrom.startFromTitle", "Start from")}
        </h4>
        <p className="text-xs text-muted-foreground">
          {t(
            "iam.newOrgDialog.copyFrom.startFromHint",
            "Setting up a sibling of an org you already run? Copy its setup instead of re-entering it.",
          )}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <StartTile
          selected={mode === "blank"}
          onClick={() => onModeChange("blank")}
          icon={<File className="h-3.5 w-3.5" />}
          title={t("iam.newOrgDialog.copyFrom.blankTitle", "Blank")}
          description={t(
            "iam.newOrgDialog.copyFrom.blankDescription",
            "Only Adge’s default access.",
          )}
        />
        <StartTile
          selected={mode === "copy"}
          onClick={() => onModeChange("copy")}
          icon={<Copy className="h-3.5 w-3.5" />}
          title={t("iam.newOrgDialog.copyFrom.copyTitle", "Copy an existing org")}
          description={t(
            "iam.newOrgDialog.copyFrom.copyDescription",
            "Industries, brand defaults and roles.",
          )}
        />
      </div>

      {mode === "copy" ? (
        <>
          <div className="relative">
            <OrgPickerCombobox
              items={items}
              value={value}
              onValueChange={onValueChange}
              disabled={disabled || loading}
              placeholder={
                sourceName ??
                t("iam.newOrgDialog.copyFrom.sourcePlaceholder", "Select an organization…")
              }
              searchPlaceholder={t(
                "iam.newOrgDialog.copyFrom.sourceSearchPlaceholder",
                "Search organizations…",
              )}
              emptyMessage={t("iam.newOrgDialog.copyFrom.sourceEmpty", "No organizations found")}
              ariaLabel={t(
                "iam.newOrgDialog.copyFrom.sourceAria",
                "Copy settings from organization",
              )}
            />
            {loading ? (
              <Loader2
                className="pointer-events-none absolute right-9 top-1/2 h-3.5 w-3.5 -translate-y-1/2 animate-spin text-muted-foreground"
                aria-hidden
              />
            ) : null}
          </div>
          {sourceMeta ? (
            <p className="-mt-1 text-[11px] text-muted-foreground">{sourceMeta}</p>
          ) : null}

          <div className="overflow-hidden rounded-lg border border-border">
            <div className="flex items-center justify-between border-b border-border bg-muted/40 px-3 py-2">
              <span className="font-mono text-[10px] uppercase tracking-[0.04em] text-muted-foreground">
                {t("iam.newOrgDialog.copyFrom.whatToCopy", "What to copy")}
              </span>
              <span className="flex items-center gap-2">
                <span className="text-[11px] text-muted-foreground">
                  {t("iam.newOrgDialog.copyFrom.selectedCount", "{{selected}} of {{total}} selected", {
                    selected: selectedCount,
                    total: SECTION_KEYS.length,
                  })}
                </span>
                <button
                  type="button"
                  onClick={onSelectAll}
                  disabled={selectedCount === SECTION_KEYS.length}
                  className="text-[11px] font-medium text-primary hover:underline disabled:pointer-events-none disabled:text-muted-foreground"
                >
                  {t("iam.newOrgDialog.copyFrom.addAll", "Add all")}
                </button>
              </span>
            </div>
            {rows.map((row) => {
              const checked = sections[row.key];
              return (
                <button
                  key={row.key}
                  type="button"
                  role="checkbox"
                  aria-checked={checked}
                  onClick={() => onSectionChange(row.key, !checked)}
                  className="flex w-full items-start gap-3 border-b border-border-subtle px-3 py-2.5 text-left last:border-b-0 hover:bg-muted/30"
                >
                  <span
                    className={cn(
                      "mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] border",
                      checked
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-input-border bg-background",
                    )}
                    aria-hidden
                  >
                    {checked ? (
                      <Check className="h-3 w-3 text-primary-foreground" strokeWidth={3} />
                    ) : null}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-xs font-medium text-foreground">
                      {t(row.titleKey, row.titleDefault)}
                    </span>
                    <span className="block text-[11px] text-muted-foreground">{row.detail}</span>
                  </span>
                </button>
              );
            })}
          </div>

          <p className="flex items-start gap-2 text-[11px] leading-snug text-muted-foreground">
            <Ban className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
            {t(
              "iam.newOrgDialog.copyFrom.neverCopied",
              "Never copied: members, customer partition, BigQuery products, audit history and the Retool ID.",
            )}
          </p>
        </>
      ) : null}
    </section>
  );
}

function StartTile({
  selected,
  onClick,
  icon,
  title,
  description,
}: {
  selected: boolean;
  onClick: () => void;
  icon: ReactNode;
  title: string;
  description: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex items-start gap-3 rounded-lg border p-3 text-left transition-colors",
        selected ? "border-primary bg-primary-subtle" : "border-border bg-background hover:bg-muted/40",
      )}
    >
      <span
        className={cn(
          "mt-0.5 h-4 w-4 shrink-0 rounded-full border-[5px]",
          selected ? "border-primary bg-background" : "border-input-border bg-background",
        )}
        aria-hidden
      />
      <span className="min-w-0">
        <span className="flex items-center gap-1.5 text-[13px] font-medium text-foreground">
          {icon}
          {title}
        </span>
        <span className="mt-0.5 block text-xs text-muted-foreground">{description}</span>
      </span>
    </button>
  );
}
